import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "@phosphor-icons/react";

// Persisted on this device/browser so the banner behaves like a real "app
// install" prompt instead of a nagging popup:
//  - LS_INSTALLED = "1"  -> we've confirmed the app is on the home screen.
//    While this is set, the banner never shows again.
//  - LS_DISMISSED = "1"  -> the user has already been offered the banner
//    once (and closed it / declined it) without installing. While this is
//    set, we don't keep re-showing it on every login.
// Both flags live in localStorage, so a different browser/device (or a
// fresh profile with cleared storage) starts clean and gets the prompt
// again -- which is also what happens for a "new user" on their own phone.
const LS_INSTALLED = "sfcgo_pwa_installed";
const LS_DISMISSED = "sfcgo_pwa_dismissed";

const getInstalledFlag = () => localStorage.getItem(LS_INSTALLED) === "1";
const setInstalledFlag = (v) => (v ? localStorage.setItem(LS_INSTALLED, "1") : localStorage.removeItem(LS_INSTALLED));
const getDismissedFlag = () => localStorage.getItem(LS_DISMISSED) === "1";
const setDismissedFlag = (v) => (v ? localStorage.setItem(LS_DISMISSED, "1") : localStorage.removeItem(LS_DISMISSED));

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

function isAndroidChrome() {
  const ua = window.navigator.userAgent;
  // Real Chrome on Android (excludes in-app webviews like FB/Instagram/WhatsApp,
  // which report Chrome-like UAs but never fire beforeinstallprompt).
  return /android/i.test(ua) && /chrome\//i.test(ua) && !/wv\)/i.test(ua) && !/; ?wv/i.test(ua);
}

// Pages where the install prompt is allowed to appear -- the login screen
// and the dashboard home (since login redirects straight to "/").
const PROMPT_PATHS = new Set(["/login", "/"]);

// How long to wait for the native beforeinstallprompt before falling back to
// manual "tap the menu" instructions on Android.
const ANDROID_FALLBACK_DELAY_MS = 1500;

export default function InstallBanner() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [androidHint, setAndroidHint] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone() || getInstalledFlag());

  // Capture the native install prompt for the whole app lifetime -- Chrome
  // fires beforeinstallprompt at most once per page load, so we grab it
  // whenever it happens and reuse it every time we re-arm the banner below.
  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // The browser firing this event is the authoritative signal that it
      // does NOT currently consider the app installed. If we previously
      // thought it was installed, that means the home-screen icon was
      // removed since -- treat it as a fresh install opportunity again.
      setInstalled((wasInstalled) => {
        if (wasInstalled) {
          setInstalledFlag(false);
          setDismissedFlag(false);
        }
        return false;
      });
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setInstalled(true);
      setInstalledFlag(true);
      setDeferredPrompt(null);
      setVisible(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Re-check whenever the user lands on the login page or the dashboard.
  // Only actually shows the banner if the app isn't installed yet AND the
  // user hasn't already been offered (and closed) it before on this device.
  useEffect(() => {
    if (installed || isStandalone()) {
      setVisible(false);
      return;
    }

    if (!PROMPT_PATHS.has(location.pathname)) {
      setVisible(false);
      return;
    }

    if (getDismissedFlag()) {
      setVisible(false);
      return;
    }

    setIosHint(false);
    setAndroidHint(false);

    if (deferredPrompt) {
      // Real native prompt is ready -- show the actual Install button.
      setVisible(true);
      return;
    }

    if (isIOS()) {
      // iOS Safari never fires beforeinstallprompt -- show manual steps.
      setIosHint(true);
      setVisible(true);
      return;
    }

    if (isAndroidChrome()) {
      // Give the browser a moment to fire beforeinstallprompt; if it
      // doesn't, fall back to manual "tap the menu" instructions instead
      // of showing nothing.
      const timer = setTimeout(() => {
        setAndroidHint(true);
        setVisible(true);
      }, ANDROID_FALLBACK_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, deferredPrompt, installed]);

  const dismiss = () => {
    setVisible(false);
    setDismissedFlag(true);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setInstalled(true);
        setInstalledFlag(true);
      } else {
        // Declined the native dialog -- don't nag again on every login.
        setDismissedFlag(true);
      }
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
      setInstalling(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-banner"
      className="fixed bottom-0 inset-x-0 z-50 bg-[#0C0C0D] text-white px-4 py-3 flex items-center gap-3 shadow-2xl border-t border-white/10"
    >
      <div className="w-9 h-9 rounded-md bg-white flex items-center justify-center p-1 shrink-0">
        <img src="/sfc-logo.png" alt="SFC" className="w-full h-full object-contain" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">Add SFC Go to Home Screen</div>
        <div className="text-xs text-white/60">Open instantly, works offline</div>
      </div>
      {iosHint ? (
        <div className="text-xs text-white/70 max-w-[170px] leading-snug">
          Tap Share, then "Add to Home Screen"
        </div>
      ) : androidHint ? (
        <div className="text-xs text-white/70 max-w-[190px] leading-snug">
          Tap the menu, then "Install app" / "Add to Home screen"
        </div>
      ) : (
        <button
          onClick={install}
          disabled={installing}
          data-testid="install-banner-button"
          className="signal-btn px-4 py-2 rounded-md text-sm font-bold shrink-0 disabled:opacity-60"
        >
          {installing ? "Installing..." : "Install"}
        </button>
      )}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="install-banner-dismiss"
        className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-white/10 shrink-0"
      >
        <X size={16} weight="bold" />
      </button>
    </div>
  );
}